import { ApiResponse, RosterStudent, SchoolClass } from "src/models";
import {
  Accommodation,
  AccommodationPayload,
  AddStudentPayload,
  ClassTeacherView,
  CreateClassPayload,
  SchoolClassSummary,
  UpdateStudentPayload,
} from "src/models/hierarchy";
import { HistoryEntry, HistoryPageArgs } from "src/models/history";
import {
  BulkResult,
  ClassListItem,
  ListClassesArgs,
  Page,
  toQueryString,
} from "src/models/staff";
import {
  BulkStudentsPayload,
  Enrollment,
  ListStudentsArgs,
  MoveStudentPayload,
  StudentDetail,
  StudentSummary,
} from "src/models/students";
import { apiSlice } from "src/store/apiSlice";

const base = "/api/account/v1";

/** A student's History; every change to the student invalidates it. */
const historyTag = (studentId: string) => ({
  type: "Students" as const,
  id: `history:${studentId}`,
});

/** What an enrollment change touches: the student, the lists and each class's roster. */
const enrollmentTags = (studentId: string, classIds: string[]) => [
  { type: "Students" as const, id: `student:${studentId}` },
  historyTag(studentId),
  { type: "Students" as const, id: "list" },
  ...classIds.flatMap((classId) => [
    { type: "Students" as const, id: classId },
    { type: "Classes" as const, id: classId },
  ]),
  "Classes" as const,
];

export const classesApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getMyClasses: builder.query<SchoolClass[], void>({
      query: () => ({ url: `${base}/classes/mine`, method: "GET" }),
      transformResponse: (response: ApiResponse<{ classes: SchoolClass[] }>) =>
        response.data.classes,
      providesTags: ["Classes"],
    }),
    /** Every class in the caller's scope, paged (`GET /v1/classes`). Platform admins send `districtId`. */
    listClasses: builder.query<Page<ClassListItem>, ListClassesArgs>({
      query: (args) => ({
        url: `${base}/classes${toQueryString(args)}`,
        method: "GET",
      }),
      transformResponse: (response: ApiResponse<Page<ClassListItem>>) =>
        response.data,
      providesTags: ["Classes"],
    }),
    getClass: builder.query<SchoolClass, string>({
      query: (classId) => ({
        url: `${base}/classes/${classId}`,
        method: "GET",
      }),
      transformResponse: (response: ApiResponse<{ class: SchoolClass }>) =>
        response.data.class,
      providesTags: (_r, _e, classId) => [{ type: "Classes", id: classId }],
    }),
    listSchoolClasses: builder.query<
      SchoolClassSummary[],
      { schoolId: string; includeInactive?: boolean }
    >({
      query: ({ schoolId, includeInactive }) => ({
        url: `${base}/schools/${schoolId}/classes${includeInactive ? "?includeInactive=true" : ""}`,
        method: "GET",
      }),
      transformResponse: (
        response: ApiResponse<{ classes: SchoolClassSummary[] }>,
      ) => response.data.classes,
      providesTags: (_r, _e, { schoolId }) => [
        "Classes",
        { type: "Classes", id: `school:${schoolId}` },
      ],
    }),
    createClass: builder.mutation<
      SchoolClass,
      { schoolId: string; body: CreateClassPayload }
    >({
      query: ({ schoolId, body }) => ({
        url: `${base}/schools/${schoolId}/classes`,
        method: "POST",
        body,
      }),
      transformResponse: (response: ApiResponse<{ class: SchoolClass }>) =>
        response.data.class,
      invalidatesTags: ["Classes"],
    }),
    updateClass: builder.mutation<
      SchoolClass,
      {
        classId: string;
        body: Partial<CreateClassPayload> & { active?: boolean };
      }
    >({
      query: ({ classId, body }) => ({
        url: `${base}/classes/${classId}`,
        method: "PATCH",
        body,
      }),
      transformResponse: (response: ApiResponse<{ class: SchoolClass }>) =>
        response.data.class,
      invalidatesTags: (_r, _e, { classId }) => [
        "Classes",
        { type: "Classes", id: classId },
      ],
    }),
    getClassTeachers: builder.query<ClassTeacherView[], string>({
      query: (classId) => ({
        url: `${base}/classes/${classId}/teachers`,
        method: "GET",
      }),
      transformResponse: (
        response: ApiResponse<{ teachers: ClassTeacherView[] }>,
      ) => response.data.teachers,
      providesTags: (_r, _e, classId) => [
        { type: "Classes", id: `teachers:${classId}` },
      ],
    }),
    setClassTeachers: builder.mutation<
      ClassTeacherView[],
      {
        classId: string;
        teachers: { profileId: string; role?: "teacher" | "aide" }[];
      }
    >({
      query: ({ classId, teachers }) => ({
        url: `${base}/classes/${classId}/teachers`,
        method: "PUT",
        body: { teachers },
      }),
      transformResponse: (
        response: ApiResponse<{ teachers: ClassTeacherView[] }>,
      ) => response.data.teachers,
      invalidatesTags: (_r, _e, { classId }) => [
        "Classes",
        { type: "Classes", id: `teachers:${classId}` },
      ],
    }),
    getRoster: builder.query<RosterStudent[], string>({
      query: (classId) => ({
        url: `${base}/classes/${classId}/students`,
        method: "GET",
      }),
      transformResponse: (
        response: ApiResponse<{ students: RosterStudent[] }>,
      ) => response.data.students,
      providesTags: (_result, _error, classId) => [
        { type: "Students", id: classId },
      ],
    }),
    /**
     * Adds a student to a class. A likely duplicate at the school comes back as 409
     * `POSSIBLE_DUPLICATE` with `candidates`, unless `confirmNew` is set.
     */
    addStudent: builder.mutation<
      RosterStudent & StudentSummary,
      { classId: string; body: AddStudentPayload & { confirmNew?: boolean } }
    >({
      query: ({ classId, body }) => ({
        url: `${base}/classes/${classId}/students`,
        method: "POST",
        body,
      }),
      transformResponse: (
        response: ApiResponse<{ student: RosterStudent & StudentSummary }>,
      ) => response.data.student,
      invalidatesTags: (_r, _e, { classId }) => [
        { type: "Students", id: classId },
        { type: "Students", id: "list" },
        "Classes",
      ],
    }),
    /** The student directory in the caller's scope (`GET /v1/students`). */
    listStudents: builder.query<Page<StudentSummary>, ListStudentsArgs>({
      query: (args) => ({
        url: `${base}/students${toQueryString(args)}`,
        method: "GET",
      }),
      transformResponse: (response: ApiResponse<Page<StudentSummary>>) =>
        response.data,
      providesTags: (result) => [
        { type: "Students", id: "list" },
        ...(result?.items ?? []).map((student) => ({
          type: "Students" as const,
          id: `student:${student.id}`,
        })),
      ],
    }),
    enrollStudent: builder.mutation<
      Enrollment[],
      { studentId: string; classId: string }
    >({
      query: ({ studentId, classId }) => ({
        url: `${base}/students/${studentId}/enrollments`,
        method: "POST",
        body: { classId },
      }),
      transformResponse: (
        response: ApiResponse<{ enrollments: Enrollment[] }>,
      ) => response.data.enrollments,
      invalidatesTags: (_r, _e, { studentId, classId }) =>
        enrollmentTags(studentId, [classId]),
    }),
    /** Refused with 409 `ROSTER_MANAGED` when the class came from the roster. */
    withdrawStudent: builder.mutation<
      Enrollment[],
      { studentId: string; classId: string }
    >({
      query: ({ studentId, classId }) => ({
        url: `${base}/students/${studentId}/enrollments/${classId}`,
        method: "DELETE",
      }),
      transformResponse: (
        response: ApiResponse<{ enrollments: Enrollment[] }>,
      ) => response.data.enrollments,
      invalidatesTags: (_r, _e, { studentId, classId }) =>
        enrollmentTags(studentId, [classId]),
    }),
    /** Withdraw from a class the school created and enroll in another, in one step. */
    moveStudent: builder.mutation<Enrollment[], MoveStudentPayload>({
      query: ({ studentId, fromClassId, toClassId }) => ({
        url: `${base}/students/${studentId}/move`,
        method: "POST",
        body: { fromClassId, toClassId },
      }),
      transformResponse: (
        response: ApiResponse<{ enrollments: Enrollment[] }>,
      ) => response.data.enrollments,
      invalidatesTags: (_r, _e, { studentId, fromClassId, toClassId }) =>
        enrollmentTags(studentId, [fromClassId, toClassId]),
    }),
    /** A school-added student comes back active with no enrollments. */
    reactivateStudent: builder.mutation<StudentDetail, string>({
      query: (studentId) => ({
        url: `${base}/students/${studentId}/reactivate`,
        method: "POST",
      }),
      transformResponse: (response: ApiResponse<{ student: StudentDetail }>) =>
        response.data.student,
      invalidatesTags: (_r, _e, studentId) => [
        { type: "Students", id: `student:${studentId}` },
        historyTag(studentId),
        { type: "Students", id: "list" },
        "Classes",
      ],
    }),
    /** Each id is applied on its own; the result lists every item. */
    bulkStudents: builder.mutation<BulkResult, BulkStudentsPayload>({
      query: (body) => ({
        url: `${base}/students/bulk`,
        method: "POST",
        body,
      }),
      transformResponse: (response: ApiResponse<BulkResult>) => response.data,
      invalidatesTags: (_r, _e, { ids, classId }) => [
        "Students",
        ...ids.map((id) => ({
          type: "Students" as const,
          id: `student:${id}`,
        })),
        "Classes",
        ...(classId ? [{ type: "Classes" as const, id: classId }] : []),
      ],
    }),
    getStudent: builder.query<StudentDetail, string>({
      query: (studentId) => ({
        url: `${base}/students/${studentId}`,
        method: "GET",
      }),
      transformResponse: (response: ApiResponse<{ student: StudentDetail }>) =>
        response.data.student,
      providesTags: (_r, _e, studentId) => [
        { type: "Students", id: `student:${studentId}` },
      ],
    }),
    /**
     * `expectedUpdatedAt` in the body turns a concurrent change into 409 `STALE_RECORD`; a
     * roster-owned field is refused with 409 `ROSTER_MANAGED`.
     */
    updateStudent: builder.mutation<
      StudentDetail,
      { studentId: string; classId?: string; body: UpdateStudentPayload }
    >({
      query: ({ studentId, body }) => ({
        url: `${base}/students/${studentId}`,
        method: "PATCH",
        body,
      }),
      transformResponse: (response: ApiResponse<{ student: StudentDetail }>) =>
        response.data.student,
      invalidatesTags: (_r, _e, { studentId, classId }) => [
        { type: "Students", id: `student:${studentId}` },
        historyTag(studentId),
        { type: "Students", id: "list" },
        ...(classId
          ? [{ type: "Students" as const, id: classId }]
          : ["Students" as const]),
      ],
    }),
    deactivateStudent: builder.mutation<
      StudentDetail,
      { studentId: string; classId?: string }
    >({
      query: ({ studentId }) => ({
        url: `${base}/students/${studentId}`,
        method: "DELETE",
      }),
      transformResponse: (response: ApiResponse<{ student: StudentDetail }>) =>
        response.data.student,
      invalidatesTags: (_r, _e, { studentId, classId }) => [
        { type: "Students", id: `student:${studentId}` },
        historyTag(studentId),
        { type: "Students", id: "list" },
        ...(classId
          ? [{ type: "Students" as const, id: classId }]
          : ["Students" as const]),
        "Classes",
      ],
    }),
    getAccommodation: builder.query<Accommodation | null, string>({
      query: (studentId) => ({
        url: `${base}/students/${studentId}/accommodations`,
        method: "GET",
      }),
      transformResponse: (
        response: ApiResponse<{ accommodation: Accommodation | null }>,
      ) => response.data.accommodation,
      providesTags: (_r, _e, studentId) => [
        { type: "Students", id: `accommodation:${studentId}` },
      ],
    }),
    setAccommodation: builder.mutation<
      Accommodation,
      { studentId: string; classId?: string; body: AccommodationPayload }
    >({
      query: ({ studentId, body }) => ({
        url: `${base}/students/${studentId}/accommodations`,
        method: "PUT",
        body,
      }),
      transformResponse: (
        response: ApiResponse<{ accommodation: Accommodation }>,
      ) => response.data.accommodation,
      invalidatesTags: (_r, _e, { studentId, classId }) => [
        { type: "Students", id: `accommodation:${studentId}` },
        historyTag(studentId),
        ...(classId
          ? [{ type: "Students" as const, id: classId }]
          : ["Students" as const]),
      ],
    }),
    /** Who changed what on the record, newest first (`GET /v1/students/:id/history`). */
    getStudentHistory: builder.query<
      Page<HistoryEntry>,
      { studentId: string } & HistoryPageArgs
    >({
      query: ({ studentId, limit, offset }) => ({
        url: `${base}/students/${studentId}/history${toQueryString({ limit, offset })}`,
        method: "GET",
      }),
      transformResponse: (response: ApiResponse<Page<HistoryEntry>>) =>
        response.data,
      providesTags: (_r, _e, { studentId }) => [historyTag(studentId)],
    }),
  }),
});

export const {
  useGetMyClassesQuery,
  useListClassesQuery,
  useGetClassQuery,
  useListSchoolClassesQuery,
  useCreateClassMutation,
  useUpdateClassMutation,
  useGetClassTeachersQuery,
  useSetClassTeachersMutation,
  useGetRosterQuery,
  useLazyGetRosterQuery,
  useAddStudentMutation,
  useListStudentsQuery,
  useEnrollStudentMutation,
  useWithdrawStudentMutation,
  useMoveStudentMutation,
  useReactivateStudentMutation,
  useBulkStudentsMutation,
  useGetStudentQuery,
  useUpdateStudentMutation,
  useDeactivateStudentMutation,
  useGetStudentHistoryQuery,
  useGetAccommodationQuery,
  useSetAccommodationMutation,
} = classesApiSlice;
